import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronRight } from 'lucide-react';
import { useEscapeClose } from '../hooks/useEscapeClose';

export interface MenuItem {
  label?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  active?: boolean;
  submenu?: MenuItem[];
  divider?: boolean;
  disabled?: boolean;
  customRender?: React.ReactNode;  // If set, render this instead of default label+icon
}

export interface ContextMenuProps {
  items: MenuItem[];
  position: { x: number; y: number };
  onClose: () => void;
}

function SubMenu({ items, parentRect, onClose }: {
  items: MenuItem[];
  parentRect: DOMRect;
  onClose: () => void;
}) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [adjusted, setAdjusted] = useState<{ x: number; y: number }>({
    x: parentRect.right,
    y: parentRect.top,
  });

  useEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let x = parentRect.right;
    let y = parentRect.top;

    // Flip left if overflowing right edge
    if (x + rect.width > window.innerWidth) {
      x = parentRect.left - rect.width;
    }
    // Flip up if overflowing bottom edge
    if (y + rect.height > window.innerHeight) {
      y = window.innerHeight - rect.height - 4;
    }
    // Clamp to top
    if (y < 4) y = 4;

    setAdjusted({ x, y });
  }, [parentRect]);

  return (
    <div
      ref={menuRef}
      role="menu"
      className="fixed bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-[10001] min-w-[160px]"
      style={{ left: adjusted.x, top: adjusted.y }}
    >
      {items.map((item, index) => (
        <MenuItemRow key={index} item={item} onClose={onClose} />
      ))}
    </div>
  );
}

function MenuItemRow({ item, onClose }: { item: MenuItem; onClose: () => void }) {
  const rowRef = useRef<HTMLDivElement>(null);
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = useCallback(() => {
    if (item.submenu && !item.disabled) {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = setTimeout(() => setSubmenuOpen(true), 150);
    }
  }, [item.submenu, item.disabled]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => setSubmenuOpen(false), 200);
  }, []);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  if (item.divider) {
    return <div className="border-t border-gray-200 my-1" />;
  }

  // Custom render: render the provided element instead of default menu item
  if (item.customRender) {
    return (
      <div
        ref={rowRef}
        className="relative px-3 py-1.5"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {item.customRender}
      </div>
    );
  }

  const hasSubmenu = item.submenu && item.submenu.length > 0;

  const handleClick = () => {
    if (item.disabled) return;
    if (hasSubmenu) return;
    item.onClick?.();
    onClose();
  };

  return (
    <div
      ref={rowRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        role="menuitem"
        onClick={handleClick}
        disabled={item.disabled}
        className={`
          w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left
          ${item.disabled
            ? 'text-gray-400 cursor-not-allowed'
            : 'text-gray-700 hover:bg-gray-100 cursor-pointer'
          }
        `}
      >
        {item.icon && (
          <span className="w-4 h-4 flex items-center justify-center shrink-0">
            {item.icon}
          </span>
        )}
        <span className="flex-1 truncate">{item.label}</span>
        {item.active && (
          <Check className="w-4 h-4 text-blue-600 shrink-0" />
        )}
        {hasSubmenu && (
          <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
        )}
      </button>
      {hasSubmenu && submenuOpen && rowRef.current && (
        <SubMenu
          items={item.submenu!}
          parentRect={rowRef.current.getBoundingClientRect()}
          onClose={onClose}
        />
      )}
    </div>
  );
}

export function ContextMenu({ items, position, onClose }: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [adjusted, setAdjusted] = useState<{ x: number; y: number }>(position);
  const leaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEscapeClose(onClose);

  // Adjust position to stay on screen
  useEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let x = position.x;
    let y = position.y;

    if (x + rect.width > window.innerWidth) {
      x = position.x - rect.width;
    }
    if (y + rect.height > window.innerHeight) {
      y = position.y - rect.height;
    }
    // Clamp to edges
    if (x < 4) x = 4;
    if (y < 4) y = 4;

    setAdjusted({ x, y });
  }, [position]);

  // Close on outside click
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    // Use mousedown so the menu closes before the click event propagates
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [onClose]);

  // Cleanup leave timeout on unmount
  useEffect(() => {
    return () => {
      if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current);
    };
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current);
    leaveTimeoutRef.current = setTimeout(() => onClose(), 1000);
  }, [onClose]);

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      className="fixed bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-[10000] min-w-[160px]"
      style={{ left: adjusted.x, top: adjusted.y }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {items.map((item, index) => (
        <MenuItemRow key={index} item={item} onClose={onClose} />
      ))}
    </div>,
    document.body
  );
}
